import { useRef, useState, memo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import CustomNodeHandle from "layouts/Flow Designer/components/CustomNodes/components/CustomNodeHandle";
import { showStartNodePanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import TriggersList from "./TriggersList";
import ButtonTriggers from "./ButtonTriggers";
import NodeIcon from "./NodeIcon";
import NodeLabel from "./NodeLabel";
import { NODE_TYPES } from "constants/flowDesigner";
import { clearNodesHighlight } from "layouts/Flow Designer/helpers/common";
import { executeFlow } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { useAuth0 } from "@auth0/auth0-react";
import {
  showComponentPropsPanel,
  hideComponentPropsPanel
} from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";
import {
  closeTriggerPropertiesPanel,
  setIsTitlebarExecuteTriggerListOpen,
  setExecuteSelectedTriggerId
} from "redux/actions/flowDesigner/triggersActionCreator";

const styles = {
  nodeWrapper: {
    position: "relative",
    display: "flex"
  },
  card: () => ({
    order: 1,
    position: "relative"
  }),
  contentWrapper: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    padding: "14px"
  }
};

function StaticNode({ data }) {
  const { isVerticalCanvasDirection } = useSelector((state) => state?.designer?.settings);
  const [nodeStartTriggerListOpen, setNodeStartTriggerListOpen] = useState(false);
  const lblClass = isVerticalCanvasDirection ? "right-side" : "top-side";
  const nodeRef = useRef();
  const dispatch = useDispatch();
  const { isExecuting } = useSelector((store) => store?.designer?.tabs?.currentFlowTab);
  const { flowExecutionFailed } = useSelector((store) => store?.designer?.flowExecution);
  const { canvasFlowInstance, currentSavedFlow } = useSelector((state) => state?.designer?.flows);
  const { user } = useAuth0();
  const { addLayoutTab } = useFlexLayout();
  const { t } = useTranslation("flowDesigner");

  const handleAddLayoutTab = (tab) => () => {
    addLayoutTab(tab, `startNodeProps`, `Start Node`);
    dispatch(closeTriggerPropertiesPanel());
  };

  const handleOpenStartNodePanel = () => {
    if (data?.isStart) {
      dispatch(showStartNodePanel());
    }
  };

  const handleLogsTab = (tab) => () => {
    addLayoutTab(tab, `executionReport`, `Logs`);
  };

  const handleExecute = () => {
    if (isExecuting) {
      return;
    }

    if (!currentSavedFlow) {
      toast.error(t("messages.not_saved_flow"));
      return;
    }

    const { nodes } = canvasFlowInstance;
    const nonStaticNodes = nodes.filter((node) => node.type !== NODE_TYPES.STATIC);

    if (nonStaticNodes.length === 0) {
      toast.warning(t("messages.add_components"));
      return;
    }

    const enabledNodes = nonStaticNodes.filter((node) => !node.data?.isDisabled);

    if (enabledNodes.length === 0) {
      toast.warning(t("messages.enable_components"));
      return;
    }

    clearNodesHighlight();
    dispatch(
      executeFlow(user?.stripeId, {
        flow_id: currentSavedFlow.id,
        targetUserId: user?.cxfUserId
      })
    );

    handleLogsTab(FLEX_LAYOUT_COMPONENT.executionReport)();
    dispatch(setExecuteSelectedTriggerId(null));
    dispatch(setIsTitlebarExecuteTriggerListOpen(false));
    hideComponentProps();
  };

  const hideComponentProps = () => {
    if (showComponentPropsPanel) {
      dispatch(hideComponentPropsPanel());
    }
  };

  useEffect(() => {
    if (flowExecutionFailed) {
      toast.error(t("messages.execution_failed"));
    }
  }, [flowExecutionFailed]);

  return (
    <SoftBox
      sx={styles.nodeWrapper}
      className={`custom-node-wrapper custom-node-wrapper-cover static-node ${
        isVerticalCanvasDirection && "isVertical"
      }`}
      ref={nodeRef}
    >
      <Card
        sx={(theme) => styles.card(theme)}
        className={`custom-node-card static`}
        onClick={handleAddLayoutTab(FLEX_LAYOUT_COMPONENT.startNode)}
        style={{ cursor: data?.isStart ? "pointer" : "default" }}
      >
        <SoftBox sx={styles.contentWrapper}>
          <NodeIcon isStart={data.isStart} />
          <NodeLabel label={data?.label} lblClass={lblClass} />
        </SoftBox>

        {nodeStartTriggerListOpen && (
          <TriggersList
            nodeRef={nodeRef}
            setNodeStartTriggerListOpen={setNodeStartTriggerListOpen}
          />
        )}
      </Card>
      {/* added target handler for start node so goto component can create a line */}
      <CustomNodeHandle isTarget />
      {data?.isStart && (
        <>
          <CustomNodeHandle />
          <SoftBox className={`custom-node-actions static`} sx={{ mt: 2.2 }}>
            <ButtonTriggers
              openTriggersMenu={handleOpenStartNodePanel}
              handleExecute={handleExecute}
              isExecuting={isExecuting}
              showDialog={() => {}}
              closeDialog={() => {}}
            />
          </SoftBox>
        </>
      )}
    </SoftBox>
  );
}
export default memo(StaticNode);
